// client/src/components/pages/LexiStar/NotificationRules/TenantSettings.tsx
// (converted from .jsx, ST-00206)
//
// LexiStar tenant settings (m3-contracts §3.2 tenant_settings): how long a
// deviation may wait for a decision before escalating, how long a dispatched
// trip may go unacknowledged, the review threshold for derived records, the
// optional automatic sign-off, and the default driver language.
//
// ST-00206: the driver acceptance reminders (tenant_settings.ack_reminders,
// migration 034) — 0 none, 1 the reminder only, 2 the reminder and the last
// reminder when the alert escalates. Shown and sent only when the server
// reports the setting (a server or database without 034 keeps the old form).

import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import ThemedSelectJs from '../../../common/ThemedSelect';
import type { ThemedSelectProps } from '../../../../types/commonComponents';
import {
  ACK_REMINDER_LEVELS,
  type AckRemindersLevel,
  type LexiStarTenantSettings,
  type LexiStarTenantSettingsPatch,
} from '../../../../types/lexistar-tenant-settings';
import { inputClasses, cardClasses } from '../StatusBoard/atoms';

import { getTenantSettings, updateTenantSettings, pickObject } from './notificationsApi';

// The facade module (types/commonComponents) would pull the map into this page: types only.
const ThemedSelect = ThemedSelectJs as unknown as ComponentType<ThemedSelectProps>;

// The shared input classes carry w-full; fixed-width fields drop it.
const fixedWidthInput = inputClasses.replace('w-full ', '');

const FIELDS = [
  'deviation_decision_sla_s',
  'ack_timeout_s',
  'review_threshold',
  'auto_signoff_days',
  'driver_default_language',
] as const;

/** The reminder choices, in the order the select lists them. */
export const ACK_REMINDER_OPTIONS: ReadonlyArray<{ value: AckRemindersLevel; label: string }> = [
  { value: 2, label: 'Reminder and last reminder' },
  { value: 1, label: 'One reminder only' },
  { value: 0, label: 'No reminders' },
];

/** The form as the inputs hold it (numbers as typed). */
interface SettingsForm {
  sla_min: number | string;
  ack_min: number | string;
  review_threshold: number | string;
  auto_signoff_days: number | string;
  driver_default_language: string;
  /** null: the server does not report the setting (before migration 034) — not shown, not sent. */
  ack_reminders: AckRemindersLevel | null;
}

type NumberKey = 'sla_min' | 'ack_min' | 'review_threshold' | 'auto_signoff_days';

interface Message {
  ok: boolean;
  text: string;
}

const isLevel = (v: unknown): v is AckRemindersLevel =>
  ACK_REMINDER_LEVELS.includes(v as AckRemindersLevel);

/** The API error text of a failed request, else `fallback`. */
function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: unknown } } } | null;
  const text = e?.response?.data?.error;
  return typeof text === 'string' && text ? text : fallback;
}

export default function TenantSettings({ canManage }: { canManage: boolean }) {
  const [saved, setSaved] = useState<SettingsForm | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res: unknown = await getTenantSettings();
      const s = (pickObject(res, 'settings', 'tenant_settings') || {}) as LexiStarTenantSettings;
      const reminders =
        s.ack_reminders === undefined || s.ack_reminders === null ? null : Number(s.ack_reminders);
      const f: SettingsForm = {
        sla_min:
          s.deviation_decision_sla_s != null ? Math.round(s.deviation_decision_sla_s / 60) : 10,
        ack_min: s.ack_timeout_s != null ? Math.round(s.ack_timeout_s / 60) : 15,
        review_threshold: s.review_threshold ?? 70,
        auto_signoff_days: s.auto_signoff_days ?? '',
        driver_default_language: s.driver_default_language || 'en',
        ack_reminders: reminders === null ? null : isLevel(reminders) ? reminders : 2,
      };
      setSaved(f);
      setForm(f);
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Failed to load the settings.') });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!form) {
    return (
      <section className={`${cardClasses} p-6`} aria-label="Tenant settings">
        <p className={`text-sm ${message ? 'text-red-700' : 'text-slate-500'}`}>
          {message?.text || 'Loading settings...'}
        </p>
      </section>
    );
  }

  const set = (patch: Partial<SettingsForm>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const invalid =
    !(Number(form.sla_min) >= 1) ||
    !(Number(form.ack_min) >= 1) ||
    !(Number(form.review_threshold) >= 0 && Number(form.review_threshold) <= 100) ||
    (form.auto_signoff_days !== '' &&
      !(Number(form.auto_signoff_days) >= 1 && Number(form.auto_signoff_days) <= 365));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const all: Required<Omit<LexiStarTenantSettingsPatch, 'ack_reminders'>> = {
      deviation_decision_sla_s: Math.round(Number(form.sla_min) * 60),
      ack_timeout_s: Math.round(Number(form.ack_min) * 60),
      review_threshold: Number(form.review_threshold),
      auto_signoff_days: form.auto_signoff_days === '' ? null : Number(form.auto_signoff_days),
      driver_default_language: form.driver_default_language,
    };
    const body: LexiStarTenantSettingsPatch = {
      ...Object.fromEntries(FIELDS.map((k) => [k, all[k]])),
      ...(form.ack_reminders !== null ? { ack_reminders: form.ack_reminders } : {}),
    };
    try {
      await updateTenantSettings(body);
      setSaved(form);
      setMessage({ ok: true, text: 'Settings saved.' });
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'The settings were not saved.') });
    } finally {
      setSaving(false);
    }
  };

  const field = (id: string, label: string, hint: string, input: ReactNode) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-800">
        {label}
      </label>
      <p className="text-xs text-slate-500 mb-1">{hint}</p>
      {input}
    </div>
  );
  const numberInput = (
    id: string,
    key: NumberKey,
    props: { min?: number; max?: number; placeholder?: string } = {}
  ) => (
    <input
      id={id}
      type="number"
      value={form[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      className={`${fixedWidthInput} w-32`}
      disabled={!canManage}
      {...props}
    />
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  return (
    <section className={`${cardClasses} p-6 space-y-5`} aria-label="Tenant settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {field(
          'set-sla',
          'Deviation decision time (min)',
          'Unanswered deviations escalate after this.',
          numberInput('set-sla', 'sla_min', { min: 1 })
        )}
        {field(
          'set-ack',
          'Trip acknowledgement time (min)',
          'A dispatched trip not accepted by then is flagged "not acknowledged".',
          numberInput('set-ack', 'ack_min', { min: 1 })
        )}
        {form.ack_reminders !== null &&
          field(
            'set-ack-reminders',
            'Driver acceptance reminders',
            'When a trip is flagged "not acknowledged" the driver gets a reminder; the last reminder goes when the alert escalates.',
            <div className="w-64">
              <ThemedSelect
                id="set-ack-reminders"
                value={String(form.ack_reminders)}
                onChange={(e) => {
                  const level = Number(e.target.value);
                  if (isLevel(level)) set({ ack_reminders: level });
                }}
                className={inputClasses}
                disabled={!canManage}
              >
                {ACK_REMINDER_OPTIONS.map((o) => (
                  <option key={o.value} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          )}
        {field(
          'set-review',
          'Review threshold (confidence %)',
          'Derived records below this go to the review queue and are not counted until resolved.',
          numberInput('set-review', 'review_threshold', { min: 0, max: 100 })
        )}
        {field(
          'set-signoff',
          'Automatic sign-off (days)',
          'Leave empty to sign trips off by hand only.',
          numberInput('set-signoff', 'auto_signoff_days', { min: 1, max: 365, placeholder: 'off' })
        )}
        {field(
          'set-lang',
          'Default driver language',
          'Used when a driver has no language set.',
          <div className="w-48">
            <ThemedSelect
              id="set-lang"
              value={form.driver_default_language}
              onChange={(e) => set({ driver_default_language: e.target.value })}
              className={inputClasses}
              disabled={!canManage}
            >
              <option value="en">English</option>
              <option value="af">Afrikaans</option>
            </ThemedSelect>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
        <p role="status" className={`text-sm ${message?.ok ? 'text-emerald-700' : 'text-red-700'}`}>
          {message?.text ||
            (invalid ? 'Check the values: times of at least a minute, a threshold of 0–100.' : '')}
        </p>
        {canManage && (
          <button
            type="button"
            onClick={save}
            disabled={saving || invalid || !dirty}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400"
          >
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        )}
      </div>
    </section>
  );
}
